/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VehicleSystems.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import VehicleSystemsAddDialog from "../../components/labcar/VehicleSystemsAddDialog";
import VehicleSystemsEditDialog from "../../components/labcar/VehicleSystemsEditDialog";
import { HandleApiActions, labCar } from "../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { selectVehicleSystemAction } from "../../redux/actions/labCarActions";
import {
  vlcCardStyles,
  vlcComponentStyle,
} from "../../components/customComponentsV2/vlc/config/vlcConfig";
import VlcCardHeader from "../../components/customComponentsV2/vlc/cards/VlcCardHeader";
import { componentStyle } from "../../config/appTheme";
import { Search } from "@mui/icons-material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { archiveReasons, PROJECT_STATUS } from "../../config/appConstants";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import VlcCardContent from "../../components/customComponentsV2/vlc/cards/VlcCardContent";
import CustomPaginationV2 from "../../components/customComponentsV2/common/pagination/CustomPaginationV2";
import CustomToolTip from "../../components/infoButton/CustomToolTip";
import { showToastMessage } from "../../redux/actions/actionHandler";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomCheckBoxV2 from "../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";
import DetailsLogTab from "../../components/customComponentsV2/vlc/pages/DetailsLogTab";
import CommonInputSwitch from "../../components/customComponentsV2/common/InputSwitch/CommonInputSwitch";

import PropTypes from "prop-types";

function VehicleSystems({
  getZoneVSCountList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  selectedZoneDetails,
  variantOptionList,
  supplierOptionsList,
  vsOptionsList,
  filterState,
  onFilterChange,
  onClearFilters,
  selectedVehicleSystem,
  setSelectedVehicleSystem,
  selectedArchivedVSs,
  setSelectedArchivedVSs,
  selectedArchivedVCs,
  viewArchived,
  clearAllCounter,
  retrievalRefreshCounter,
}) {
  const dispatch = useDispatch();
  const reduxProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const projectDetails = selectedProjectDetails || reduxProjectDetails;
  const vehicleSystems = useSelector(
    (state) => state.labCarReducer.vehicleSystems,
  );
  const selectedVehicleSystemId = useSelector(
    (state) => state.labCarReducer.selectedVehicleSystemId,
  );
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [copyVehicleSystemDetails, setCopyVehicleSystemDetails] =
    useState(null);
  const [selectedSearchField, setSelectedSearchField] = useState("name");
  const [searchFieldValue, setSearchFieldValue] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeItemId, setActiveItemId] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [openRecreationVSDialog, setOpenRecreationVSDialog] = useState(false);
  const [recreationParentVSList, setRecreationParentVSList] = useState([]);
  const [selectedParentVSDetails, setSelectedParentVSDetails] = useState(null);
  const [openEditVSDialog, setOpenEditVSDialog] = useState(false);
  const [selectedVSItemForEdit, setSelectedVSItemForEdit] = useState(null);
  const [openVSChangeLogsDialog, setOpenVSChangeLogsDialog] = useState(false);
  const [vsChangeLogsList, setVSChangeLogsList] = useState([]);
  const [vsChangeLogsTotalCount, setVSChangeLogsTotalCount] = useState(0);
  const [vsLogsDialogData, setVSLogsDialogData] = useState(null);
  const [confirmArchiveDialogOpen, setConfirmArchiveDialogOpen] =
    useState(false);
  const [itemToArchive, setItemToArchive] = useState(null);
  const [vsDetailsDialogTabs, setVsDetailsDialogTabs] = useState([
    "Details",
    "Log",
    "Attribute",
  ]);

  const [isArchived, setIsArchived] = useState(Boolean(viewArchived));

  useEffect(() => {
    setIsArchived(Boolean(viewArchived));
  }, [viewArchived]);

  const handleArchiveToggle = (checked) => {
    const nextVal = typeof checked === "boolean" ? checked : !isArchived;
    setIsArchived(nextVal);
    getVehicleSystems({ archive: nextVal }).catch(console.error);
  };

  const handleArchivedVSCheckboxChange = (vsId, checked) => {
    if (checked) {
      setSelectedArchivedVSs((prev) => [...(prev || []), vsId]);
    } else {
      setSelectedArchivedVSs((prev) => (prev || []).filter((id) => id !== vsId));
    }
  };

  const getVehicleSystems = React.useCallback(async (options = {}) => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const isZonal = selectedProjectDetails?.is_zonal;
    const zonePosition = selectedZoneDetails?.position;
    if (!projectId || !variantId) return;
    if (isZonal && (zonePosition === null || zonePosition === undefined))
      return;
    const apiDetails = labCar.get_vehicle_systems;
    const archiveOption =
      typeof options?.archive === "boolean" ? options.archive : isArchived;
    dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: false,
        params: {
          project_id: Number(projectId),
          variant_id: Number(variantId),
          ...(isZonal && { zone_position: zonePosition }), // Add zone position if zonal
          ...(searchFieldValue && { [selectedSearchField]: searchFieldValue }), // Add search filter if available
          sort_field: filterState?.sortField,
          sort_order: filterState?.sortOrder,
          ...(filterState?.nameFilterSearch && {
            name: filterState.nameFilterSearch,
          }),
          ...(filterState?.assemblyNumFilterSearch && {
            assembly_number: filterState.assemblyNumFilterSearch,
          }),
          ...(filterState?.descriptionFilterSearch && {
            description: filterState.descriptionFilterSearch,
          }),
          page_number: page,
          page_size: rowsPerPage,
          archive: !!archiveOption,
        },
      }),
    ).then((resp) => {
      if (resp?.error) {
        setTotalCount(0);
        return;
      }

      setTotalCount(Number(resp?.total_count ?? 0));
      setSelectedVehicleSystem(resp?.data[0]);
    });
  }, [
    dispatch,
    selectedProjectDetails?.project_id,
    selectedSingleVariantDetails?.variant_id,
    selectedProjectDetails?.is_zonal,
    selectedZoneDetails?.position,
    selectedSearchField,
    searchFieldValue,
    filterState?.sortField,
    filterState?.sortOrder,
    filterState?.nameFilterSearch,
    filterState?.assemblyNumFilterSearch,
    filterState?.descriptionFilterSearch,
    page,
    rowsPerPage,
    setSelectedVehicleSystem,
    isArchived,
  ]);


  const getParentVSDetails = () => {
    dispatch(
      HandleApiActions({
        ...labCar.get_parent_vs,
        params: {
          project_id: selectedProjectDetails?.project_id,
          parent_project_id: selectedProjectDetails?.parent_project_id,
          variant_id: selectedSingleVariantDetails?.variant_id,
          parent_variant_id:
            selectedSingleVariantDetails?.parent_variant_details?.variant_id,
        },
      }),
    ).then((parentEEResponse) => {
      if (parentEEResponse?.error) {
        showToastMessage({
          type: "error",
          message: parentEEResponse.msg,
        });
        setRecreationParentVSList([]);
        setSelectedParentVSDetails(null);
      } else {
        setSelectedParentVSDetails(parentEEResponse?.data?.[0] || null);
        setRecreationParentVSList(parentEEResponse?.data || []);
        setOpenRecreationVSDialog(true);
      }
    });
  };

  const handleVSRecreateOpen = () => {
    getParentVSDetails();
  };

  const handleGetVehicleSystemChangeLogs = ({
    pageNo = 1,
    pageSize = 25,
  } = {}) => {
    const entityId = vsLogsDialogData?.id;
    if (!entityId) return;

    dispatch(
      HandleApiActions({
        ...labCar.vehicle_system_logs,
        params: {
          entity_id: Number(entityId),
          page_number: pageNo,
          page_size: pageSize,
        },
        show_toast: false,
      }),
    ).then((resp) => {
      if (resp?.error) {
        setVSChangeLogsList([]);
        setVSChangeLogsTotalCount(0);
        return;
      }

      setVSChangeLogsList(resp?.data || []);
      setVSChangeLogsTotalCount(resp?.total_count || 0);
    });
  };

  const handleVSLogsDialogOpen = (item) => {
    setVSLogsDialogData(item);
    setOpenVSChangeLogsDialog(true);
  };

  const handleVSViewDialogOpen = (item) => {
    setVSLogsDialogData(item);
    setVsDetailsDialogTabs(["Details", "Attribute"]);
    setOpenVSChangeLogsDialog(true);
  };

  const handleVSLogsDialogClose = () => {
    setOpenVSChangeLogsDialog(false);
    setVSLogsDialogData(null);
    setVSChangeLogsList([]);
    setVSChangeLogsTotalCount(0);
    setVsDetailsDialogTabs(["Details", "Log", "Attribute"]);
  };

  const handleVehicleSystemCopyOpen = () => {
    if (!vsLogsDialogData) return;

    setCopyVehicleSystemDetails(vsLogsDialogData);
    setOpenVSChangeLogsDialog(false);
    setOpenAddDialog(true);
  };

  useEffect(() => {
    getVehicleSystems().catch(console.error);
  }, [
    page,
    rowsPerPage,
    selectedSearchField,
    searchFieldValue,
    selectedProjectDetails,
    selectedSingleVariantDetails,
    selectedZoneDetails,
    filterState?.sortField,
    filterState?.sortOrder,
    filterState?.nameFilter,
    filterState?.assemblyNumFilter,
    filterState?.descriptionFilter,
    filterState?.nameFilterSearch,
    filterState?.assemblyNumFilterSearch,
    filterState?.descriptionFilterSearch,
    getVehicleSystems,
  ]);

  useEffect(() => {
    if (retrievalRefreshCounter > 0) {
      getVehicleSystems().catch(console.error);
    }
  }, [retrievalRefreshCounter, getVehicleSystems]);

  useEffect(() => {
    setSelectedSearchField("name");
    setSearchFieldValue("");
    setPage(1);
  }, [selectedProjectDetails, selectedSingleVariantDetails]);


  const handleSelectVehicleSystem = (vsId, item) => {
    if (vsId) {
      dispatch(selectVehicleSystemAction(vsId));
    }
    setSelectedVehicleSystem(item);
  };

  const checkIsVariantArchived = (selectedSingleVariantDetails, item) => {
    const isStatusArchived = (val) =>
      typeof val === "string" && val.toUpperCase() === "ARCHIVED";

    if (
      isStatusArchived(selectedSingleVariantDetails?.archive_status) ||
      isStatusArchived(selectedSingleVariantDetails?.archive_reason) ||
      isStatusArchived(selectedSingleVariantDetails?.status) ||
      isStatusArchived(item?.variant_details?.archive_status) ||
      isStatusArchived(item?.variant_archive_status)
    ) {
      return true;
    }

    if (
      Array.isArray(selectedSingleVariantDetails?.archive_reason) &&
      selectedSingleVariantDetails.archive_reason.some(
        (r) =>
          typeof r === "string" &&
          (r.toUpperCase() === "ARCHIVED" || r.toUpperCase() === "VARIANT_ARCHIVED"),
      )
    ) {
      return true;
    }

    if (
      Array.isArray(item?.archive_reason) &&
      item.archive_reason.some((r) => {
        const str = String(r).toUpperCase();
        return str.includes("VARIANT") || str === "ARCHIVED";
      })
    ) {
      return true;
    }

    return false;
  };

  const getVSArchiveTooltipTitle = (missingArchiveReasons, isVariantArchived) => {
    const displayMissingReasons = missingArchiveReasons.filter(
      (r) =>
        !String(r).toUpperCase().includes("VARIANT") &&
        String(r).toUpperCase() !== "ARCHIVED",
    );
    let displayReasons = [];
    if (displayMissingReasons.length > 0) {
      displayReasons = displayMissingReasons;
    } else if (!isVariantArchived && missingArchiveReasons.length > 0) {
      displayReasons = missingArchiveReasons;
    }

    return [
      displayReasons.length > 0 ? `Missing ${displayReasons.join(", ")}` : "",
      isVariantArchived ? "Varient is not active" : "",
    ]
      .filter(Boolean)
      .join(", ");
  };

  const renderVSArchiveErrorIcon = (item) => {
    const hasMissingArchiveReasons =
      Array.isArray(item?.archive_reason) &&
      item.archive_reason.some(
        (r) => !archiveReasons?.vs_exceptionToActive?.includes(r),
      );
    const missingArchiveReasons = hasMissingArchiveReasons
      ? item.archive_reason.filter(
          (r) => !archiveReasons?.vs_exceptionToActive?.includes(r),
        )
      : [];

    const isVariantArchived = checkIsVariantArchived(
      selectedSingleVariantDetails,
      item,
    );

    if (!hasMissingArchiveReasons && !isVariantArchived) return null;

    const tooltipTitle = getVSArchiveTooltipTitle(
      missingArchiveReasons,
      isVariantArchived,
    );

    return (
      <CustomToolTip type="component" title={tooltipTitle}>
        <ErrorOutlineIcon sx={{ fontSize: "18px", color: "red", mr: 0.5 }} />
      </CustomToolTip>
    );
  };
  const handleMoreClick = (e, item) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setActiveItemId(item?.id);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActiveItemId(null);
  };
  const handleMenuAction = (action, item) => {
    handleCloseMenu();
    if (action === "edit") {
      setSelectedVSItemForEdit(item);
      setOpenEditVSDialog(true);
      return;
    }
    if (action === "view") {
      handleVSViewDialogOpen(item);
      return;
    }
    if (action === "log") {
      setVsDetailsDialogTabs(["Log"]);
      handleVSLogsDialogOpen(item);
      return;
    }
    if (action === "archive") {
      setItemToArchive(item);
      setConfirmArchiveDialogOpen(true);
    }
  };

  const handleConfirmArchiveVS = async () => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const item = itemToArchive;
    if (!projectId || !variantId || !item?.id) {
      setConfirmArchiveDialogOpen(false);
      setItemToArchive(null);
      return;
    }

    try {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.archive_vehicle_system,
            params: {
              vs_ids: [item.id],
              project_id: Number(projectId),
              variant_id: Number(variantId),
            },
            show_toast: true,
          }),
        ),
      );
      if (!res?.error) {
        getVehicleSystems({ archive: isArchived }).catch(console.error);
        if (typeof getZoneVSCountList === "function") {
          getZoneVSCountList();
        }
      }
    } catch (err) {
      console.error("Error archiving vehicle system:", err);
    } finally {
      setConfirmArchiveDialogOpen(false);
      setItemToArchive(null);
    }
  };

  const handlePageChange = (value) => setPage(value);

  const [nameSearch, setNameSearch] = useState("");
  const debounceRef = useRef();

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (nameSearch.length >= 3 || nameSearch.length === 0) {
      debounceRef.current = setTimeout(() => {
        setSelectedSearchField("name");
        setSearchFieldValue(nameSearch); // Triggers API
        setPage(1);
      }, 500);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [nameSearch]);

  return (
    <Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Card // sx={styles.eeCompCard}
          sx={{
            ...vlcComponentStyle.card,
            display: "flex",
            flexDirection: "column",
            height: "450px",
          }}
        >
          <CardHeader
            sx={vlcComponentStyle.cardHeader}
            title={
              <VlcCardHeader
                title={"Vehicle Systems"}
                titleAction={
                  viewArchived ? (
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#ffffff",
                          fontFamily: "Open Sans",
                          userSelect: "none",
                        }}
                      >
                        {isArchived ? "Archived" : "Active"}
                      </Typography>
                      <CommonInputSwitch
                        checked={isArchived}
                        onChange={handleArchiveToggle}
                      />
                    </Box>
                  ) : null
                }
                showRecreateIcon={Boolean(
                  selectedProjectDetails?.parent_project_id &&
                    selectedSingleVariantDetails?.parent_variant_details
                      ?.variant_id,
                )}
                onRecreateClick={handleVSRecreateOpen}
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={1}
                  sx={{
                    width: "100%",
                    mt: 1,
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Search Name"
                    variant="outlined"
                    fullWidth
                    value={nameSearch}
                    onChange={(e) => setNameSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={componentStyle.searchField.textField}
                  />
                  <Box>
                    {!viewArchived && (
                      <AddButtonV2
                        handleOnClick={() => setOpenAddDialog(true)}
                        disabled={!selectedSingleVariantDetails?.variant_id}
                      />
                    )}
                  </Box>
                </Box>
              </VlcCardHeader>
            }
          />
          <CardContent
            sx={{
              ...vlcCardStyles.CardContent,
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              p: 0,
              scrollbarColor: "#888 #f1f1f1",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555",
              },
            }}
          >
            {selectedSingleVariantDetails?.variant_id &&
            vehicleSystems &&
            vehicleSystems?.length > 0 ? (
              vehicleSystems?.map((item) => (
                <VlcCardContent
                  key={item.id}
                  onClick={() => handleSelectVehicleSystem(item?.id, item)}
                  data={item}
                  isSelected={selectedVehicleSystemId === item?.id}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "stretch",
                    }}
                  >
                    {item?.archive_status?.toUpperCase() === "ARCHIVED" && (
                        <Box 
                          sx={{ 
                            p: 1, 
                            pb: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }} 
                          onClick={(e) => e.stopPropagation()}
                        >
                          <CustomCheckBoxV2
                            noPaddingRequired={true}
                            key={`vs-${item.id}-${clearAllCounter}`}
                            value={selectedArchivedVSs?.includes(item.id)}
                            disabled={selectedArchivedVCs?.length > 0}
                            onChange={(e) => handleArchivedVSCheckboxChange(item.id, e.target.checked)}
                          />
                        </Box>
                      )}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        p: 1,
                        width: "90%",
                      }}
                    >
                      <Typography
                        sx={{
                          ...componentStyle.font.text,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                          color: vlcCardStyles.vlcCardListColor,
                        }}
                      >
                        <CustomToolTip
                          type="string"
                          length={60}
                          style={{
                            ...componentStyle.font.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                            fontWeight: "bold",
                          }}
                          title={`Assembly No: ${item?.assembly_number ?? ""}`}
                        >
                          {`Assembly No: ${item?.assembly_number ?? ""}`}
                        </CustomToolTip>
                      </Typography>

                      <Typography
                        sx={{
                          ...componentStyle.font.text,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                        }}
                      >
                        <CustomToolTip
                          type="string"
                          length={50}
                          style={{
                            ...componentStyle.font.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                          }}
                          title={`Name: ${item?.name ?? ""}`}
                        >
                          {`Name: ${item?.name ?? ""}`}
                        </CustomToolTip>
                      </Typography>

                      <Typography
                        sx={{
                          ...componentStyle.font.text,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                          color: vlcCardStyles.vlcCardListColor,
                        }}
                      >
                        <CustomToolTip
                          type="string"
                          length={60}
                          style={{
                            ...componentStyle.font.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                          }}
                          title={`Description: ${item?.description ?? ""}`}
                        >
                          {`Description: ${item?.description ?? ""}`}
                        </CustomToolTip>
                      </Typography>
                    </Box>
                    <Box sx={{ pr: 1, pt: 1, display: "flex", alignItems: "center" }}>
                      {renderVSArchiveErrorIcon(item)}
                      <IconButton
                        size="small"
                        sx={{ color: "black" }}
                        onClick={(e) => handleMoreClick(e, item)}
                      >
                        <MoreVertIcon sx={{ fontSize: "18px" }} />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && activeItemId === item?.id}
                        onClose={handleCloseMenu}
                        slotProps={{
                          paper: {
                            sx: {
                              borderRadius: 0,
                              boxShadow: 1,
                            },
                          },
                        }}
                      >
                        <MenuItem
                          onClick={() => handleMenuAction("view", item)}
                          sx={{ ...componentStyle.font.cardText }}
                        >
                          View
                        </MenuItem>
                        <MenuItem
                          onClick={() => handleMenuAction("edit", item)}
                          sx={{ ...componentStyle.font.cardText }}
                        >
                          Edit details
                        </MenuItem>
                        <MenuItem
                          onClick={() => handleMenuAction("log", item)}
                          sx={{ ...componentStyle.font.cardText }}
                        >
                          Log
                        </MenuItem>
                        {item?.archive_status?.toUpperCase() !== "ARCHIVED" &&
                          projectDetails?.status === PROJECT_STATUS.DRAFT &&
                          Boolean(projectDetails?.archive_vlc) && (
                          <MenuItem
                            onClick={() => handleMenuAction("archive", item)}
                            sx={{ ...componentStyle.font.cardText }}
                          >
                            Archive
                          </MenuItem>
                        )}
                      </Menu>
                    </Box>
                  </Box>
                </VlcCardContent>
              ))
            ) : (
              <Typography variant="body1" sx={styles.noDataAvailable}>
                No data available
              </Typography>
            )}
          </CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              minHeight: "40px",
              borderTop: "1px solid #ddd",
              borderRadius: "0px !important",
            }}
          >
            {(totalCount || vehicleSystems?.length || 0) > 0 && (
              <CustomPaginationV2
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                totalItems={Math.max(
                  totalCount || 0,
                  vehicleSystems?.length || 0,
                )}
              />
            )}
          </Box>
        </Card>
      </Box>

      <VehicleSystemsAddDialog
        openDialog={openAddDialog}
        setOpenDialog={setOpenAddDialog}
        title={"Add Vehicle System"}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        selectedZoneDetails={selectedZoneDetails}
        supplierOptionsList={supplierOptionsList}
        vsOptionsList={vsOptionsList}
        getVehicleSystems={getVehicleSystems}
        getZoneVSCountList={getZoneVSCountList}
        copyVehicleSystemDetails={copyVehicleSystemDetails}
        onCopyPrefillClear={() => setCopyVehicleSystemDetails(null)}
      />

      <VehicleSystemsAddDialog
        openDialog={openRecreationVSDialog}
        setOpenDialog={setOpenRecreationVSDialog}
        title={"Recreate Vehicle System"}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        selectedZoneDetails={selectedZoneDetails}
        supplierOptionsList={supplierOptionsList}
        vsOptionsList={vsOptionsList}
        getVehicleSystems={getVehicleSystems}
        getZoneVSCountList={getZoneVSCountList}
        recreationMode={true}
        recreationParentVSList={recreationParentVSList}
        selectedParentVSDetails={selectedParentVSDetails}
        setSelectedParentVSDetails={setSelectedParentVSDetails}
      />
      <VehicleSystemsEditDialog
        openDialog={openEditVSDialog}
        setOpenDialog={setOpenEditVSDialog}
        title={isArchived ? "Draft Vehicle System" : "Edit Vehicle System"}
        viewArchived={isArchived}
        selectedVSItem={selectedVSItemForEdit}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        selectedZoneDetails={selectedZoneDetails}
        supplierOptionsList={supplierOptionsList}
        vsOptionsList={vsOptionsList}
        getVehicleSystems={getVehicleSystems}
        getZoneVSCountList={getZoneVSCountList}
      />

      <CustomDialogBoxV2
        open={openVSChangeLogsDialog}
        onClose={handleVSLogsDialogClose}
        title={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <Typography sx={{ ...componentStyle.font.cardHeadder }}>
              Vehicle Systems
            </Typography>
            <IconButton
              size="small"
              onClick={handleVehicleSystemCopyOpen}
              disabled={!vsLogsDialogData}
              aria-label="Copy Vehicle System"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        <Box sx={{ width: "450px", height: "500px" }}>
          <DetailsLogTab
            logData={vsLogsDialogData}
            ChangeLogsList={vsChangeLogsList}
            getLogsData={handleGetVehicleSystemChangeLogs}
            selectedComponent={vsLogsDialogData}
            zone_name={vsLogsDialogData?.zone_details?.zone_name}
            variantOptionList={variantOptionList}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            openEditDetailsDialog={openVSChangeLogsDialog}
            setOpenEditDetailsDialog={setOpenVSChangeLogsDialog}
            totalLogsCount={vsChangeLogsTotalCount}
            type={"VS"}
            visibleTabs={vsDetailsDialogTabs}
          />
        </Box>
      </CustomDialogBoxV2>

      {confirmArchiveDialogOpen && (
        <CustomDialogBoxV2
          open={confirmArchiveDialogOpen}
          onClose={() => {
            setConfirmArchiveDialogOpen(false);
            setItemToArchive(null);
          }}
          title="Confirm Changes"
          // dialogRadius="0rem"
          successButtonName="Confirm"
          rejectButtonName="Cancel"
          actionButtonStyle="continuous"
          onClickSuccess={handleConfirmArchiveVS}
          onClickReject={() => {
            setConfirmArchiveDialogOpen(false);
            setItemToArchive(null);
          }}
        >
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "left", textAlign: "left", gap: 1 }}>
            <Typography>
             This change will affect the downstream components, modules and intersections, if any are linked to the element.
            </Typography>
            <Typography>
              Are you sure you want to proceed?
            </Typography>
          </Box>
        </CustomDialogBoxV2>
      )}
    </Box>
  );
}

VehicleSystems.propTypes = {
  getZoneVSCountList: PropTypes.func,
  selectedProjectDetails: PropTypes.shape({
    project_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    parent_project_id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    is_zonal: PropTypes.bool,
  }),
  selectedSingleVariantDetails: PropTypes.shape({
    variant_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    parent_variant_details: PropTypes.shape({
      variant_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    archive_status: PropTypes.string,
    archive_reason: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    status: PropTypes.string,
  }),
  selectedZoneDetails: PropTypes.shape({
    position: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  variantOptionList: PropTypes.array,
  supplierOptionsList: PropTypes.array,
  vsOptionsList: PropTypes.array,
  filterState: PropTypes.shape({
    sortField: PropTypes.string,
    sortOrder: PropTypes.string,
    nameFilter: PropTypes.string,
    assemblyNumFilter: PropTypes.string,
    descriptionFilter: PropTypes.string,
    nameFilterSearch: PropTypes.string,
    assemblyNumFilterSearch: PropTypes.string,
    descriptionFilterSearch: PropTypes.string,
  }),
  onFilterChange: PropTypes.func,
  onClearFilters: PropTypes.func,
  selectedVehicleSystem: PropTypes.object,
  setSelectedVehicleSystem: PropTypes.func,
  selectedArchivedVSs: PropTypes.arrayOf(PropTypes.any),
  setSelectedArchivedVSs: PropTypes.func,
  selectedArchivedVCs: PropTypes.arrayOf(PropTypes.any),
  viewArchived: PropTypes.bool,
  clearAllCounter: PropTypes.number,
  retrievalRefreshCounter: PropTypes.number,
};

export default VehicleSystems;

const styles = {
  vsContainer: {
    flex: 1,
    minWidth: 0,
    minHeight: { xs: "350px", sm: "400px", md: "500px" },
    maxHeight: { xs: "500px", sm: "600px", md: "700px" },
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "stretch",
    height: "100%",
    boxSizing: "border-box",
  },
  card: {
    flex: 1,
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    height: "100%",
    boxShadow: 4,
    borderRadius: "3%",
    display: "flex",
    flexDirection: "column",
  },
  cardHeader: {
    py: { xs: 0.3, sm: 0.5 },
    backgroundColor: "lightgrey",
    borderBottom: "1px solid lightgrey",
    borderRadius: "2%",
  },
  cardContent: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    scrollbarColor: "#888 #f1f1f1",
    scrollbarWidth: "thin",
    px: { xs: 0.5, sm: 1 },
    py: { xs: 0.5, sm: 1 },
  },
  paginationPaper: {
    display: "flex",
    justifyContent: "flex-end",
    p: { xs: "2px", sm: "3px" },
  },
  noDataAvailable: {
    textAlign: "center",
    mt: { xs: 1, sm: 2 },
    fontSize: { xs: "0.9rem", sm: "1rem" },
  },
  cardContentCards: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: { xs: 0.5, sm: 1 },
    pl: { xs: 0.5, sm: 1 },
    pr: { xs: 0.2, sm: 0.5 },
    backgroundColor: "background.paper",
    borderRadius: 2,
    boxShadow: 1,
    cursor: "pointer",
    minHeight: { xs: 44, sm: 54 },
    maxWidth: "100%",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  cardContentCardContainer: {
    textAlign: "start",
    my: { xs: 0.5, sm: 1 },
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    gap: 0.5,
  },
};
